import { useEffect, useState, useRef, ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import CalendarBackend from "../Calendar/utils/CalendarBackend";
import { setCalendars, setPlaces } from "../../store/features/cacheSlice";
import toast from "react-hot-toast";
import Datagrid from "../..//components/Datagrid/Datagrid";
import { BackendApi } from "../../utils/BackendApi";
import { SelectionChangedEvent, ColDef } from "ag-grid-community";
import "bootstrap/dist/css/bootstrap.min.css";
import common from "../..//utils/common";
import { ApiRoute } from "../..//utils/ApiRoute";
import { useWindowSize } from "../../hooks/useWindowSize";
import RecordSearchInput from "../..//components/Datagrid/RecordSearchInput";
import { SearchRecordRequest } from "../../class/record.schema";
import { AgGridReact } from "ag-grid-react";
import { SystemMsg } from "../../utils/SystemMsg";
import { ColorColumnCell } from "../../components/ColorColumnCell";
import TrashGridControl from "./TrashGridControl";
import "bootstrap/dist/css/bootstrap.css";
import { IdMultiRequest } from "../../class/request.schema";
import { TrashGridType } from "../../class/frontend.schema";
import moment from "moment";
import type { cal_calendar } from "../../class/prisma/db";

type TrashRecordGridProps = {
  toast: typeof toast;
  recordType: TrashGridType;
  setRecordTypeFunc: (type: TrashGridType) => void;
};

const TrashRecordGrid = ({
  toast,
  recordType,
  setRecordTypeFunc,
}: TrashRecordGridProps) => {
  const dispatch = useDispatch();
  const gridRef = useRef<AgGridReact>(null);

  const places = useSelector((state: RootState) => state.cache.places);
  const calendars = useSelector((state: RootState) => state.cache.calendars);

  const [placeRefresh, setPlaceRefresh] = useState(false);
  const [calendarRefresh, setCalendarRefresh] = useState(false);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [rowData, setRowData] = useState<any[]>();
  const [rowLoaded, setRowLoaded] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(common?.pageSizeArray[1]);
  const [pageCount, setPageCount] = useState(0);
  const [paginationPage, setPaginationPage] = useState(0);

  //Search
  const [keyword, setKeyword] = useState("");
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");

  const { height } = useWindowSize();
  const colDefs = [
    { field: "id", width: "70" },
    {
      field: "calendarName",
      headerName: "Calendar",
      width: "150",
      cellRenderer: (params: any) => {
        return calendarNameRenderer(params);
      },
    },
    { field: "name", width: "200" },
    {
      field: "description",
      flex: 3,
    },

    {
      field: "start_date",
      headerName: "Start",
      width: "200",
      cellRenderer: (params: any) => {
        return dateRenderer(params);
      },
    },
    {
      field: "end_date",
      headerName: "End",
      width: "200",
      cellRenderer: (params: any) => {
        return dateRenderer(params);
      },
    },
    {
      field: "place_id",
      headerName: "Place",
      width: "150",
      cellRenderer: (params: any) => {
        return placeIdRenderer(params);
      },
    },
  ] as ColDef[];

  useEffect(() => {
    if (!Array.isArray(places)) {
      CalendarBackend.getPlaceData((data) => {
        if (Array.isArray(data)) {
          dispatch(setPlaces(data));
        }
      });
      setPlaceRefresh(false);
    }
  }, [places, placeRefresh]);

  useEffect(() => {
    if (!Array.isArray(calendars) || calendarRefresh) {
      CalendarBackend.getCalendarData((data) => {
        if (Array.isArray(data)) {
          console.log(`calendars`, data);
          dispatch(setCalendars(data));
        }
      });
      setCalendarRefresh(false);
    }
  }, [calendars, calendarRefresh]);

  const calendarNameRenderer = (param: any) => {
    if (!param || !param.value) {
      return param.value;
    }
    const calendarColor = param.data.calendarColor;
    return <ColorColumnCell color={calendarColor} text={param.value} />;
  };

  const getCalendarObject = (id: number): cal_calendar | undefined => {
    if (!Array.isArray(calendars) || (calendars as any[]).length === 0) {
      return;
    }
    const list = calendars as cal_calendar[];
    const find = list.find((x) => x.id === id);
    return find;
  };

  const placeIdRenderer = (param: any) => {
    if (!places) {
      return param.value;
    }
    const list = places as any[];
    if (!Array.isArray(list) || list.length === 0) {
      return param.value;
    }
    if (!param || !param.value) {
      return param.value;
    }
    if (!param.value) {
      return param.value;
    }
    const find = list.find((x) => x.id === param.value);
    if (!find) {
      return param.value;
    }
    return find.name;
  };

  const dateRenderer = (param: any) => {
    if (!param || !param.value) {
      return "";
    }
    if (!param.value) {
      return "";
    }

    const dateObj = moment(param.value);
    if (!dateObj.isValid()) {
      return "Invalid Date";
    }
    return dateObj.format(`YYYY-MM-DD HH:mm`);
  };

  const getSearchParam = () => {
    const searchParam: SearchRecordRequest = {
      page: page > 0 ? page : 1,
      pageSize: pageSize,
      orderBy: JSON.stringify({ start_date: "desc" }),
    };
    if (
      !common.stringIsNullOrEmpty(keyword) &&
      keyword.toString().trim().length > 0
    ) {
      searchParam[`keyword`] = keyword;
    }
    if (searchStartDate.length >= 6 && moment(searchStartDate).isValid()) {
      searchParam[`start_date`] = searchStartDate;
    }
    if (searchEndDate.length >= 6 && moment(searchEndDate).isValid()) {
      searchParam[`end_date`] = searchEndDate;
    }
    return searchParam;
  };
  const getRowData = () => {
    //common.setDataGridLoading(gridRef, true);
    BackendApi.get(
      `${ApiRoute.records}/trash`,
      getSearchParam(),
      (resp) => {
        //common.setDataGridLoading(gridRef, false);
        const total =
          resp.data.total != null && !Number.isNaN(resp.data.total.toString())
            ? resp.data.total
            : 0;
        setPageCount(common.getPageCount(total, pageSize));
        if (Array.isArray(resp.data.data)) {
          setRowData(transformRowData(resp.data.data));
        } else {
          setRowData([]);
        }
        setRowLoaded(true);
      },
      (resp) => {
        // common.setDataGridLoading(gridRef, false);
        toast.error(SystemMsg.Record.failedToLoadRecord);
        console.log(resp);
        setRowLoaded(true);
      },
    );
  };

  const transformRowData = (data: any[]) => {
    const dataArray: any[] = [];
    if (!Array.isArray(data)) {
      return dataArray;
    }

    for (const item of data) {
      const calendar = getCalendarObject(item.calendar_id);
      const calendarName = calendar?.name ?? "";
      const calendarColor = calendar?.color ?? "";
      dataArray.push({
        ...item,
        calendarName: calendarName,
        calendarColor: calendarColor,
      });
    }

    return dataArray;
  };

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    if (
      !event ||
      !event.selectedNodes ||
      event.selectedNodes === null ||
      !Array.isArray(event.selectedNodes) ||
      event.selectedNodes.length === 0
    ) {
      setSelectedIds([]);
      return;
    }
    const selectedNode = event.selectedNodes;
    setSelectedIds(
      selectedNode
        .filter((x) => Number.isInteger(Number(x.data.id)))
        .map((x) => Number(x.data.id)),
    );
  };

  const RestoreRecords = () => {
    if (
      !selectedIds ||
      !Array.isArray(selectedIds) ||
      selectedIds.length === 0
    ) {
      toast.error(SystemMsg.GridUi.pleaseSelectRecords);
      return;
    }
    const request = { ids: selectedIds } as IdMultiRequest;
    BackendApi.put(
      `${ApiRoute.records}/restore`,
      request,
      (resp) => {
        const success = resp.status === 200 || resp.status === 202;
        if (!success) {
          toast.error(SystemMsg.GridUi.failedToRestoreXRecord(recordType));
          return;
        }
        toast.success(
          SystemMsg.GridUi.successfullyRestoredXRecord(
            selectedIds.length,
            recordType,
          ),
        );
        RefreshRedux();
        RemoveOnGird(selectedIds);
      },
      (resp) => {
        toast.error(SystemMsg.GridUi.failedToRestoreXRecord(recordType));
        console.log(resp);
      },
    );
  };

  const DeleteRecords = () => {
    if (
      !selectedIds ||
      !Array.isArray(selectedIds) ||
      selectedIds.length === 0
    ) {
      toast.error(SystemMsg.GridUi.pleaseSelectRecords);
      return;
    }
    const request = { ids: selectedIds } as IdMultiRequest;
    BackendApi.delete(
      `${ApiRoute.records}/delete`,
      request,
      (resp) => {
        const success = resp.status === 200 || resp.status === 202;
        if (!success) {
          toast.error(SystemMsg.GridUi.failedToDeleteXRecord(recordType));
          return;
        }
        toast.success(
          SystemMsg.GridUi.successfullyDeletedXRecord(
            selectedIds.length,
            recordType,
          ),
        );
        RefreshRedux();
        RemoveOnGird(selectedIds);
      },
      (resp) => {
        toast.error(SystemMsg.GridUi.failedToRestoreXRecord(recordType));
        console.log(resp);
      },
    );
  };

  const RefreshRedux = () => {
    CalendarBackend.getCalendarData((data) => {
      if (Array.isArray(data)) {
        dispatch(setCalendars(data));
      }
    });
  };

  const RemoveOnGird = (ids: number[]) => {
    const tempIdArray = Array.from(ids, (num) => num.toString());
    const removables = rowData?.filter((x) =>
      tempIdArray.includes(x.id.toString()),
    );
    gridRef?.current?.api.applyTransaction({ remove: removables ?? [] });
    const newRowData = rowData?.filter(
      (x) => !tempIdArray.includes(x.id.toString()),
    );
    if (newRowData !== undefined) {
      setRowData([...newRowData]);
    }
  };

  const topMenuElement = (
    <TrashGridControl
      onRestoreClick={RestoreRecords}
      onDeleteClick={DeleteRecords}
      recordType={recordType}
      setRecordTypeFunc={setRecordTypeFunc}
      disabled={
        !selectedIds || !Array.isArray(selectedIds) || selectedIds.length === 0
      }
    />
  );

  useEffect(() => {
    if (Array.isArray(calendars)) {
      getRowData();
    }
  }, [calendars]);

  useEffect(() => {
    setPage(1);
    setPaginationPage(0);
  }, [pageSize]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (rowLoaded) {
        getRowData();
      }
    }, common.DEBOUNCE_TIME);
    return () => clearTimeout(debounce);
  }, [keyword, searchStartDate, searchEndDate]);

  useEffect(() => {
    if (rowLoaded) {
      getRowData();
    }
  }, [page, pageSize]);

  const handlePageClick = (event: { selected: number }) => {
    if (!event || event?.selected == null || Number.isNaN(event.selected)) {
      return;
    }
    setPage(event.selected + 1);
    setPaginationPage(event.selected);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (
      !event ||
      !event?.target ||
      event?.target?.value == null ||
      Number.isInteger(event?.target?.value)
    ) {
      return;
    }
    setPageSize(Number(event.target.value));
  };

  const topLeftElement = (
    <RecordSearchInput
      searchValue={keyword}
      setSearchValueFunc={setKeyword}
      startDate={searchStartDate}
      setStartDateFunc={setSearchStartDate}
      endDate={searchEndDate}
      setEndDateFunc={setSearchEndDate}
    />
  );

  const getPageSizeArray = () => {
    const temp = [...common.pageSizeArray];
    temp.shift();
    return temp;
  };

  return (
    <>
      <Datagrid
        rowSelect="multiRow"
        gridRef={gridRef}
        rowData={rowData}
        colDefs={colDefs}
        onSelectionChanged={onSelectionChanged}
        topLeftElement={topLeftElement}
        topRightElement={topMenuElement}
        hasControls={true}
        hasPagination
        handlePageClick={handlePageClick}
        handlePageSizeChange={handlePageSizeChange}
        paginatePage={paginationPage}
        paginatePageCount={pageCount}
        paginatePageSizeArray={getPageSizeArray()}
        windowHeight={height}
        loading={!rowLoaded}
      />
    </>
  );
};

export default TrashRecordGrid;
